import { Link } from "react-router-dom";
import { PageHeader } from "@/components/data-display/page-header";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import { Table, TableWrapper } from "@/components/ui/table";
import { EmptyRow, Panel } from "@/features/oversight/oversight-ui";
import { WipHint } from "@/components/ui/wip-hint";

/**
 * WIP layout port (round 3, ALL-5825) of the designer's `/monitoring` screen —
 * `.design-ref/prototype/app/(enterprise-wallet)/monitoring/page.tsx` →
 * `MonitoringOverview` in `features/oversight/monitoring.tsx`.
 *
 * The layout is the designer's (header, the four statistic tiles, the active
 * alerts register and the chain/provider panels side by side); the data is not,
 * and cannot be: alerting and provider health have no backend at all
 * (context/page_pipeline/BACKEND_GAPS.md row 19), so an aggregate dashboard
 * would be partly fabricated. Every value renders the shared "not available"
 * placeholder and every table its empty state.
 *
 * The pipeline health bar the designer draws is a chart with nothing behind it,
 * so it is omitted rather than drawn with invented proportions. Chain and
 * broadcast monitoring are real routes and are linked from the panels' rows in
 * the design; only the navigation links that exist are kept.
 *
 * No endpoint is called and no monitoring datum is fabricated.
 */

/** Statistic tiles the designer draws. Labels are the design's; values are not known here. */
const MONITOR_STAT_LABELS = [
  "Source Freshness",
  "Chain Health",
  "Provider Health",
  "Broadcast Pipeline",
];

/**
 * A stat tile whose value the backend does not carry. `StatCard` takes a string
 * value and would print its own dash, but the honest value here is the shared
 * placeholder component, so this keeps `StatCard`'s shell classes and swaps only
 * the value line.
 */
function UnavailableStat({ label }: { label: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface px-5 py-[18px]">
      <div className="text-[15px] font-bold leading-[1.2] tracking-tight">
        <UnavailableValue />
      </div>
      <div className="mt-2 text-xs text-muted">{label}</div>
    </div>
  );
}

export default function MonitoringPage() {
  return (
    <div>
      <PageHeader
        title="Monitoring"
        subtitle="Chain, provider, broadcast, and alerting health across the environment."
        actions={<WipHint />}
      />

      <p className="mb-5 text-xs text-muted">
        Alerting and provider health have no backend in this environment yet — the tiles and tables below are the designed
        layout with no monitoring data.
      </p>

      <div data-testid="monitoring-stats" className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {MONITOR_STAT_LABELS.map((label) => (
          <UnavailableStat key={label} label={label} />
        ))}
      </div>

      <Panel
        title="Active Alerts"
        actions={
          <Link to="/monitoring/alerts" className="text-xs font-semibold text-primary hover:underline">
            View all
          </Link>
        }
        bodyClassName="p-0"
      >
        <TableWrapper className="rounded-none border-0">
          <Table className="dense">
            <thead>
              <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                <th className="px-4 py-2.5">ID</th>
                <th className="px-4 py-2.5">Title</th>
                <th className="px-4 py-2.5">Severity</th>
                <th className="px-4 py-2.5">Owner</th>
                <th className="px-4 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              <EmptyRow colSpan={5}>No active alerts to show.</EmptyRow>
            </tbody>
          </Table>
        </TableWrapper>
      </Panel>

      <div className="grid gap-5 lg:grid-cols-2">
        <Panel
          title="Chain Health"
          actions={
            <Link to="/monitoring/chain" className="text-xs font-semibold text-primary hover:underline">
              Chain monitoring
            </Link>
          }
          bodyClassName="p-0"
        >
          <TableWrapper className="rounded-none border-0">
            <Table className="dense">
              <thead>
                <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                  <th className="px-4 py-2.5">Chain</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Block Height</th>
                </tr>
              </thead>
              <tbody>
                <EmptyRow colSpan={3}>No chain health to show.</EmptyRow>
              </tbody>
            </Table>
          </TableWrapper>
        </Panel>

        <Panel title="Provider Health" bodyClassName="p-0">
          <TableWrapper className="rounded-none border-0">
            <Table className="dense">
              <thead>
                <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                  <th className="px-4 py-2.5">Provider</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Latency p50</th>
                </tr>
              </thead>
              <tbody>
                <EmptyRow colSpan={3}>No provider health to show.</EmptyRow>
              </tbody>
            </Table>
          </TableWrapper>
        </Panel>
      </div>
    </div>
  );
}
